// Lightweight client for the Mind Spa blog API.
// Defaults to same-origin /api (works when deployed alongside the PHP backend).
// Override via REACT_APP_API_URL for the dev server (e.g. http://localhost/mindspa/api).

const API_BASE = process.env.REACT_APP_API_URL || '/api';

async function request(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    // Required so the PHPSESSID / mindspa_student session cookie flows in
    // cross-origin dev (npm start on :3000 → PHP on :80).
    credentials: 'include',
  });

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    throw new Error(data?.error || `Request failed: ${res.status}`);
  }
  return data;
}

async function postJson(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* server didn't return JSON */ }
  if (!res.ok) {
    throw new Error((data && data.error) || `Request failed: ${res.status}`);
  }
  return data;
}

export const api = {
  getBlogs: () => request('/blogs.php'),
  getBlog: (id) => request(`/blogs.php?id=${encodeURIComponent(id)}`),
  getBlogBySlug: (slug) => request(`/blogs.php?slug=${encodeURIComponent(slug)}`),
  getBlogsByTag: (tag) => request(`/blogs.php?tag=${encodeURIComponent(tag)}`),
  submitAppointment: (payload) => postJson('/appointments.php', payload),
  submitEnquiry: (payload) => postJson('/enquiries.php', payload),
  getBooks: () => request('/books.php'),
  getBookBySlug: (slug) => request(`/books.php?slug=${encodeURIComponent(slug)}`),
  getEvents: () => request('/events.php'),
  getPastEvents: () => request('/events.php?past=1'),
  getEvent: (id) => request(`/events.php?id=${encodeURIComponent(id)}`),
  getEventBySlug: (slug) => request(`/events.php?slug=${encodeURIComponent(slug)}`),
  getActivePopup: () => request('/popups.php'),
  getGallery: (page = 1, perPage = 24) =>
    request(`/gallery.php?page=${page}&per_page=${perPage}`),
  getAwards: () => request('/awards.php'),
  getTeam: () => request('/team.php'),
  getTeamMember: (slug) => request(`/team.php?slug=${encodeURIComponent(slug)}`),
  getConsultants: () => request('/consultants.php'),
  getConsultant: (slug) => request(`/consultants.php?slug=${encodeURIComponent(slug)}`),
  getCourses: () => request('/courses.php'),
  getCourseBySlug: (slug) => request(`/courses.php?slug=${encodeURIComponent(slug)}`),
  // Student auth + enrollment (live once Phase 4 + Phase 5 land).
  studentLogin: (payload) => postJson('/student/login.php', payload),
  studentLogout: () => postJson('/student/logout.php', {}),
  studentMe: () => request('/student/me.php'),
  studentChangePassword: (payload) => postJson('/student/change-password.php', payload),
  selfEnrolCourse: (course_id) => postJson('/student/enrol.php', { course_id }),
  submitCourseEnquiry: (payload) => postJson('/course-enquiry.php', payload),
  registerForCourse: (payload) => postJson('/course-register.php', payload),
  startCoursePayment: (course_id) => postJson('/student/course-payment.php', { course_id }),
  uploadCoursePaymentProof: async ({ enrollment_id, upload_token, file }) => {
    const fd = new FormData();
    fd.append('enrollment_id', enrollment_id);
    fd.append('upload_token', upload_token);
    fd.append('screenshot', file);
    const base = process.env.REACT_APP_API_URL || '/api';
    const res = await fetch(`${base}/student/course-payment-proof.php`, { method: 'POST', body: fd, credentials: 'include' });
    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch {}
    if (!res.ok) throw new Error((data && data.error) || `Upload failed: ${res.status}`);
    return data;
  },
  registerForEvent: (payload) => postJson('/event-register.php', payload),
  submitEventEnquiry: (payload) => postJson('/event-enquiry.php', payload),
  uploadEventPaymentProof: async ({ registration_id, upload_token, file }) => {
    const fd = new FormData();
    fd.append('registration_id', registration_id);
    fd.append('upload_token', upload_token);
    fd.append('screenshot', file);
    const res = await fetch(`${API_BASE}/event-payment-proof.php`, { method: 'POST', body: fd });
    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { /* not JSON */ }
    if (!res.ok) throw new Error((data && data.error) || `Upload failed: ${res.status}`);
    return data;
  },
};

export default api;
